/**
 * LineChartControl
 *
 * Line chart with single or multiple series
 * Supports spline, step, and straight line interpolation
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import {
  BaseChartComponent,
  type ChartDataPoint,
  extractChartData,
  getSeriesConfig,
} from './BaseChartComponent';

const LineChartControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);
  const seriesConfig = useMemo(() => getSeriesConfig(control), [control]);

  // Get line chart specific config
  const lineConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      interpolation: (config?.interpolation as 'linear' | 'spline' | 'step') || 'linear',
      showDots: config?.showDots !== false,
      showGrid: config?.showGrid !== false,
    };
  }, [control.config]);

  // Render SVG-based line chart
  const renderLines = useMemo(() => {
    if (!chartData || chartData.length === 0 || seriesConfig.length === 0) {
      return null;
    }

    const padding = 40;
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    // Get values for all series
    const allValues: number[] = [];
    chartData.forEach((d) => {
      seriesConfig.forEach((s) => {
        const val = (d as Record<string, unknown>)[s.dataKey];
        if (typeof val === 'number') {
          allValues.push(val);
        }
      });
    });

    const maxValue = Math.max(...allValues, 1);
    const minValue = Math.min(...allValues, 0);

    // Calculate scales
    const xScale = plotWidth / Math.max(chartData.length - 1, 1);
    const yRange = maxValue - minValue || 1;
    const yScale = plotHeight / yRange;

    // Helper to convert data point to SVG coordinates
    const getPoint = (dataIndex: number, seriesIndex: number): [number, number] | null => {
      const dataPoint = chartData[dataIndex];
      const series = seriesConfig[seriesIndex];
      const value = (dataPoint as Record<string, unknown>)[series.dataKey];

      if (typeof value !== 'number') {
        return null;
      }

      const x = padding + dataIndex * xScale;
      const y = height - padding - (value - minValue) * yScale;
      return [x, y];
    };

    // Helper to create path string
    const createPathString = (seriesIndex: number): string => {
      const points = chartData.map((_, dataIndex) => getPoint(dataIndex, seriesIndex)).filter(Boolean);

      if (points.length === 0) return '';

      let path = `M ${points[0]![0]} ${points[0]![1]}`;
      for (let i = 1; i < points.length; i++) {
        if (lineConfig.interpolation === 'spline') {
          // Simple curve approximation
          const prev = points[i - 1]!;
          const curr = points[i]!;
          const nextX = i < points.length - 1 ? points[i + 1]![0] : curr[0];
          const cp = [(prev[0] + curr[0]) / 2, prev[1]];
          path += ` Q ${cp[0]} ${cp[1]} ${curr[0]} ${curr[1]}`;
        } else {
          path += ` L ${points[i]![0]} ${points[i]![1]}`;
        }
      }
      return path;
    };

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid lines */}
        {lineConfig.showGrid &&
          [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = height - padding - ratio * plotHeight;
            return (
              <line
                key={`grid-${ratio}`}
                x1={padding}
                y1={y}
                x2={width - padding}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
            );
          })}

        {/* Y axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const value = Math.round(minValue + ratio * yRange);
          const y = height - padding - ratio * plotHeight;
          return (
            <text
              key={`y-label-${ratio}`}
              x={padding - 10}
              y={y}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
            >
              {value}
            </text>
          );
        })}

        {/* Y axis */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#000" />

        {/* X axis */}
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#000" />

        {/* Lines */}
        {seriesConfig.map((series, seriesIndex) => {
          const pathString = createPathString(seriesIndex);
          return (
            <path
              key={`line-${series.id}`}
              d={pathString}
              fill="none"
              stroke={series.color || '#8884d8'}
              strokeWidth="2"
            />
          );
        })}

        {/* Dots */}
        {lineConfig.showDots &&
          chartData.map((_, dataIndex) => {
            return seriesConfig.map((series, seriesIndex) => {
              const point = getPoint(dataIndex, seriesIndex);
              if (!point) return null;

              return (
                <circle
                  key={`dot-${dataIndex}-${seriesIndex}`}
                  cx={point[0]}
                  cy={point[1]}
                  r="4"
                  fill={series.color || '#8884d8'}
                  opacity="0.8"
                />
              );
            });
          })}

        {/* X axis labels */}
        {chartData.map((_, index) => {
          if (chartData.length > 15 && index % 2 !== 0) return null; // Skip labels if too many
          const x = padding + index * xScale;
          return (
            <text
              key={`x-label-${index}`}
              x={x}
              y={height - padding + 20}
              textAnchor="middle"
              fontSize="12"
              fill="#6b7280"
            >
              {index}
            </text>
          );
        })}

        {/* Legend */}
        {(control.config?.legend as Record<string, unknown> | undefined)?.visible !== false && (
          <g>
            {seriesConfig.map((series, index) => {
              const legendX = width - padding - 150;
              const legendY = padding + index * 25;
              return (
                <g key={`legend-${series.id}`}>
                  <line
                    x1={legendX}
                    y1={legendY + 8}
                    x2={legendX + 16}
                    y2={legendY + 8}
                    stroke={series.color || '#8884d8'}
                    strokeWidth="2"
                  />
                  <text x={legendX + 20} y={legendY + 13} fontSize="12" fill="#374151">
                    {series.name}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    );
  }, [chartData, seriesConfig, control.config, lineConfig]);

  return (
    <BaseChartComponent control={control} value={value} chartType="line-chart">
      <div style={{ width: '100%', height: '100%' }}>{renderLines}</div>
    </BaseChartComponent>
  );
});

LineChartControl.displayName = 'LineChartControl';

export default LineChartControl;
